import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './NewsHomePage.css'; // Styles specific to NewsHomePage

const ANI_LIST_API_URL = 'https://graphql.anilist.co';
const KITSU_API_URL = 'https://kitsu.io/api/edge/anime';
const GHIBLI_API_URL = 'https://ghibliapi.vercel.app/films';
const ANN_API_URL = 'https://cdn.animenewsnetwork.com/encyclopedia/api.xml'; // ANN API

const NewsHomePage = () => {
  const [aniListArticles, setAniListArticles] = useState([]);
  const [kitsuArticles, setKitsuArticles] = useState([]);
  const [ghibliArticles, setGhibliArticles] = useState([]);
  const [annArticles, setAnnArticles] = useState([]); // New state for ANN articles
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch AniList Data
  const fetchAniListData = async () => {
    try {
      const response = await fetch(ANI_LIST_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `
            query {
              Page(page: 1, perPage: 6) {
                media(type: ANIME) {
                  id
                  title {
                    english
                    native
                  }
                  description
                  coverImage {
                    medium
                  }
                  siteUrl
                }
              }
            }
          `
        })
      });
      const data = await response.json();
      return data?.data?.Page?.media.map(item => ({
        id: item.id,
        title: item.title.english || item.title.native,
        description: item.description,
        image: item.coverImage.medium,
        url: item.siteUrl
      })) || [];
    } catch (err) {
      console.error('AniList API Error:', err);
      return [];
    }
  };

  // Fetch Kitsu Data
  const fetchKitsuData = async () => {
    try {
      const response = await fetch(`${KITSU_API_URL}?page[limit]=6`);
      const data = await response.json();
      return data?.data.map(item => ({
        id: item.id,
        title: item.attributes.titles.en || item.attributes.titles.ja_jp,
        description: item.attributes.synopsis,
        image: item.attributes.posterImage.medium,
        url: `https://kitsu.io/anime/${item.id}`
      })) || [];
    } catch (err) {
      console.error('Kitsu API Error:', err);
      return [];
    }
  };

  // Fetch Studio Ghibli Data
  const fetchGhibliData = async () => {
    try {
      const response = await fetch(GHIBLI_API_URL);
      const data = await response.json();
      return data.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description,
        image: item.image || 'https://via.placeholder.com/150',
        url: `https://ghibliapi.vercel.app/films/${item.id}`
      })) || [];
    } catch (err) {
      console.error('Studio Ghibli API Error:', err);
      return [];
    }
  };

  // Fetch Anime News Network Data (Reports API)
  const fetchAnnData = async () => {
    try {
      const response = await fetch(`${ANN_API_URL}?id=155&type=anime&nlist=6`);
      const data = await response.text(); // Get raw XML data

      // Log the raw response for debugging
      console.log('RAW ANN API Response:', data);

      // Parse the XML response
      const parser = new DOMParser();
      const xml = parser.parseFromString(data, 'application/xml');

      // Log the parsed XML structure
      console.log('Parsed XML:', xml);

      // Check if we are parsing the XML correctly
      const entries = xml.querySelectorAll('entry');
      if (!entries.length) {
        console.log('No entries found in the response');
      }

      // Map over the entries to extract data
      const articles = Array.from(entries).map(item => ({
        id: item.querySelector('id')?.textContent,
        title: item.querySelector('title')?.textContent,
        description: item.querySelector('summary')?.textContent,
        image: 'https://via.placeholder.com/150', // Placeholder image
        url: item.querySelector('link')?.textContent,
      }));

      // Return articles or an empty array if no data is found
      return articles.length > 0 ? articles : [];

    } catch (err) {
      console.error('ANN API Error:', err);
      return [];
    }
  };

  // Fetch and combine data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [aniList, kitsu, ghibli, ann] = await Promise.all([
          fetchAniListData(),
          fetchKitsuData(),
          fetchGhibliData(),
          fetchAnnData()
        ]);
        setAniListArticles(aniList);
        setKitsuArticles(kitsu);
        setGhibliArticles(ghibli);
        setAnnArticles(ann); // Set ANN articles state
        setLoading(false);
      } catch (err) {
        console.error('Data Fetch Error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <section className="theNews-section">
      {/* First Studio Ghibli Films Row */}
      <div className="theNews-horizontal-section">
        <h2>Studio Ghibli Films</h2>
        <div className="theNews-horizontal-grid">
          {ghibliArticles.map(newsItem => (
            <div className="theNews-horizontal-item" key={newsItem.id}>
              <img
                className="theNews-horizontal-image"
                src={newsItem.image}
                alt={newsItem.title}
              />
              <div className="theNews-horizontal-content">
                <h3>{newsItem.title}</h3>
                {/* Truncate the description */}
                <p>
                  {newsItem.description.length > 100
                    ? `${newsItem.description.slice(0, 100)}...`
                    : newsItem.description}
                </p>
                <Link to={`/the-news/${encodeURIComponent(newsItem.url)}`} className="theNews-read-more">
                  Read More
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Anime News Network Section */}
      <div className="theNews-horizontal-section">
        <h2>Anime News Network</h2>
        <div className="theNews-horizontal-grid">
          {annArticles.map(newsItem => (
            <div className="theNews-horizontal-item" key={newsItem.id}>
              <img
                className="theNews-horizontal-image"
                src={newsItem.image}
                alt={newsItem.title}
              />
              <div className="theNews-horizontal-content">
                <h3>{newsItem.title}</h3>
                {/* Truncate the description */}
                <p>
                  {newsItem.description.length > 100
                    ? `${newsItem.description.slice(0, 100)}...`
                    : newsItem.description}
                </p>
                <Link to={`/the-news/${encodeURIComponent(newsItem.url)}`} className="theNews-read-more">
                  Read More
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AniList Section */}
      <div className="theNews-main-wrapper">
        <div className="theNews-main-content">
          <h1 className="theNews-title">AniList Updates</h1>
          <p className="theNews-subtitle">
            Stay updated with new anime features, articles, and more.
          </p>
          <div className="theNews-grid">
            {aniListArticles.map(newsItem => (
              <div className="theNews-grid-item" key={newsItem.id}>
                <img className="theNews-grid-image" src={newsItem.image} alt={newsItem.title} />
                <div className="theNews-grid-content">
                  <h3>{newsItem.title}</h3>
                  {/* Truncate the description */}
                  <p>{newsItem.description.length > 100 ? `${newsItem.description.slice(0, 100)}...` : newsItem.description}</p>
                  <Link to={`/the-news/${encodeURIComponent(newsItem.url)}`} className="theNews-read-more">
                    Read More
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Kitsu Section */}
        <div className="theNews-right-sidebar">
          <h2>Kitsu Highlights</h2>
          {kitsuArticles.map(newsItem => (
            <div className="theNews-sidebar-item" key={newsItem.id}>
              <img className="theNews-sidebar-image" src={newsItem.image} alt={newsItem.title} />
              <div className="theNews-sidebar-content">
                <h3>{newsItem.title}</h3>
                {/* Truncate the description */}
                <p>{newsItem.description.length > 80 ? `${newsItem.description.slice(0, 80)}...` : newsItem.description}</p>
                <Link to={`/the-news/${encodeURIComponent(newsItem.url)}`} className="theNews-read-more">
                  Read More
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewsHomePage;
